import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AsperaService } from '@adgate/common/services/aspera-service';
import { showSelectFileDialog } from '@ibm-aspera/sdk';
import { from } from 'rxjs';
import { environment } from '@env/environment';

@Component({
    standalone: true,
    selector: 'aspera-file-selector',
    templateUrl: './aspera-file-selector.html',
    styleUrl: './aspera-file-selector.scss',
    imports: [CommonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule]
})
export class AsperaFileSelector implements OnInit
{
    private asperaService: AsperaService = inject(AsperaService);
    private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

    /** File extension that accepted, same as 'accept' of <input type="file" />.
    By the default, it's set to video and image formats. */
    @Input() accept = ['mov','mxf','png','mp3'];

    /** Allow you to add handler after its completion. Bubble up response with selected files. */
    @Output() fileSelected = new EventEmitter<any>();
    @Input() multiple: boolean = false;
    @Input() required: boolean = false;
    @Input() showLabel: boolean = true;
    @Input() label: string = "Select File";
    @Input() fileFormControl!: FormControl;

    isInitializing = false;
    isReady = false;
    initError: string | null = null;
    selectedFiles: any;

    ngOnInit(): void
    {
        // Initialize Aspera when component loads
        this.isInitializing = true;

        this.asperaService.initSession(environment.appId).subscribe({
            next: (result) => {
                this.isInitializing = false;
                if (result.success) {
                    this.isReady = true;
                } else {
                    this.initError = result.message;
                }

                this.cdr.detectChanges();
            },
            error: (err) => {
                this.isInitializing = false;
                this.initError = err.message || 'Failed to initialize Aspera';
                this.cdr.detectChanges();
            }
        });
    }

    getPlaceholder(): string
    {
        if (this.isInitializing) {
            return 'Initializing Aspera...';
        }

        return 'Select a file using Aspera';
    }

    openFilePicker()
    {
        let fileFilter: any = {name: "Allowed Files", extensions: this.accept};

        if (!this.isReady) {
            console.warn('Aspera not ready yet');
            return;
        }

        from(showSelectFileDialog({title: 'Select files', multiple: this.multiple, filters: fileFilter})).subscribe({
            next: (result:any) => {
                this.selectedFiles = result;

                if( result.dataTransfer.files.length == 1 ) {
                    this.fileFormControl.setValue(this.filterFilename(result.dataTransfer.files[0].name));
                } else {
                    this.fileFormControl.setValue(`${this.filterFilename(result.dataTransfer.files[0].name)} and ${result.dataTransfer.files.length - 1} more` );
                }

                this.fileFormControl.markAsTouched();
                this.fileSelected.emit(result);
            },
            error: (result:any) => {
                console.error(result);
            }
        });
    }

    public filterFilename( path: string ): string
    {
        if (path.indexOf('\\') != -1 ) {
            let arr = path.split('\\');
            return arr[arr.length - 1];
        } else if (path.indexOf('/') != -1 ) {
            let arr = path.split('/');
            return arr[arr.length - 1];
        } else {
            return '';
        }
    }

    onClearSelection()
    {
        this.fileFormControl.setValue('');
    }
}
